import type { ClipSpan } from './DubPlacementService';

/**
 * Which part of a clip a stretch of the source video lands on.
 *
 * Shared so that placement (which turns a dub into audio elements) and coverage
 * (which counts how much of a clip has a dub) ask the same question the same
 * way. Two copies of this rule could disagree about whether a line belongs to a
 * clip, and then the figure shown to a person would not describe what they hear.
 */

/**
 * The shortest overlap worth treating as real, in seconds. A clip's boundaries
 * rarely land on a line's edge, so a line can overlap a kept span by a sliver
 * that carries no audible sound. Kept under the shortest recorded word, so no
 * whole word can be swallowed.
 */
export const MIN_PIECE_SEC = 0.01;

/**
 * The part of `[startSec, endSec)` that falls inside one kept span, measured in
 * SOURCE-video seconds.
 */
export interface SpanOverlap {
  startSec: number;
  endSec: number;
  durationSec: number;
}

export function intersectSpan(
  startSec: number,
  endSec: number,
  span: ClipSpan,
): SpanOverlap | null {
  const spanEndSec = span.sourceStartSec + span.durationSec;
  const overlapStartSec = Math.max(startSec, span.sourceStartSec);
  const overlapEndSec = Math.min(endSec, spanEndSec);
  const durationSec = overlapEndSec - overlapStartSec;
  if (durationSec < MIN_PIECE_SEC) return null;
  return { startSec: overlapStartSec, endSec: overlapEndSec, durationSec };
}

/**
 * Does a stretch of the source video appear anywhere in the clip? Answers once
 * however many spans it touches, unlike placement, which emits one piece per
 * span so that a line cut in two sounds on both sides of the cut.
 */
export function overlapsAnySpan(
  startSec: number,
  endSec: number,
  spans: ClipSpan[],
): boolean {
  return spans.some(span => intersectSpan(startSec, endSec, span) !== null);
}

/** How much of `[startSec, endSec)` the clip keeps, in seconds, across all spans. */
export function keptDurationSec(
  startSec: number,
  endSec: number,
  spans: ClipSpan[],
): number {
  return spans.reduce((total, span) => {
    const overlap = intersectSpan(startSec, endSec, span);
    return overlap ? total + overlap.durationSec : total;
  }, 0);
}
